import { AsyncPipe, CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { confirmTermsUpdateActions } from '@core/core.actions';
import { currentUserIsExternal, selectTermsUpdateCustomerSpacesIsPending } from '@core/core.selectors';
import { CustomerSpaceService } from '@core/customer-space/customer-space.service';
import { select, Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LoadingSpinnerComponent } from '@platform/components/loading-spinner/loading-spinner.component';
import * as PlatformActions from '@platform/platform.actions';
import { PlatformModule } from '@platform/platform.module';
import { LoadingService, provideLoadingService } from '@platform/shared/loading.service';
import { RootState } from '@root/app.state';
import { QuillViewComponent } from 'ngx-quill';
import { map, Observable, shareReplay, startWith } from 'rxjs';

@Component({
  selector: 'app-terms-update',
  imports: [TranslateModule, MatButtonModule, PlatformModule, AsyncPipe, QuillViewComponent, LoadingSpinnerComponent, CommonModule],
  templateUrl: './terms-update.component.html',
  styleUrl: './terms-update.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideLoadingService()],
})
export class TermsUpdateComponent implements OnInit {
  @Input() customerSpaceId!: string;
  language = 'DE-de';
  public readonly pending$: Observable<boolean>;
  public readonly isExternalUser$: Observable<boolean>;
  public rawHtml$: Observable<string>;
  public isContentLoading$: Observable<boolean>;

  private translateService = inject(TranslateService);
  private store$ = inject(Store<RootState>);
  private loadingService = inject(LoadingService);
  private custService = inject(CustomerSpaceService);

  constructor() {
    this.language = this.translateService.getCurrentLang();
    this.pending$ = this.store$.pipe(select(selectTermsUpdateCustomerSpacesIsPending));
    this.isExternalUser$ = this.store$.pipe(select(currentUserIsExternal));

    this.isContentLoading$ = this.loadingService.loading$;
    const language = this.getCurrentLanguage();
    this.rawHtml$ = this.loadingService.showLoadingUntilCompleted(this.custService.getTermsPage(language)).pipe(
      startWith({ rawHtml: '' }),
      shareReplay(1),
      map(({ rawHtml }) => rawHtml),
    );
  }

  ngOnInit(): void {
    this.store$.dispatch(PlatformActions.loadCurrentUser());
  }

  confirm() {
    this.store$.dispatch(confirmTermsUpdateActions.confirmTermsUpdate({ customerSpaceId: this.customerSpaceId }));
  }

  getCurrentLanguage() {
    if (this.language?.startsWith('en')) {
      return 'English';
    }
    return 'German';
  }
}
